import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Users, Newspaper, Package, EnvelopeSimple, TrendUp, Handshake } from "@phosphor-icons/react";

export default function Dashboard() {
  const [stats, setStats] = useState({ leads: 0, careerLeads: 0, insights: 0, products: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [leads, insights, products, an] = await Promise.all([
          api.get("/leads"),
          api.get("/insights"),
          api.get("/products"),
          api.get("/analytics/summary"),
        ]);
        setStats({
          leads: leads.data.length,
          careerLeads: leads.data.filter((l) => l.source === "career").length,
          insights: insights.data.length,
          products: products.data.length,
        });
        setRecent(leads.data.slice(0, 6));
        setAnalytics(an.data);
      } catch { /* ignore */ }
    })();
  }, []);

  const cards = [
    { icon: Users, label: "Total leads", val: stats.leads, to: "/admin/leads" },
    { icon: EnvelopeSimple, label: "Career applicants", val: stats.careerLeads, to: "/admin/leads" },
    { icon: Newspaper, label: "Blog posts", val: stats.insights, to: "/admin/insights" },
    { icon: Package, label: "Products", val: stats.products, to: "/admin/products" },
  ];

  const analyticsCards = analytics ? [
    { icon: TrendUp, label: "Leads · 7 days", val: analytics.leads_7d, sub: `${analytics.leads_30d} in last 30d` },
    { icon: TrendUp, label: "Demo requests · 7d", val: analytics.demo_7d, sub: `${analytics.demo_30d} in last 30d` },
    { icon: Handshake, label: "Won deals", val: analytics.won, sub: `${analytics.conversion_pct}% conversion` },
    { icon: Users, label: "Contact leads · 7d", val: analytics.contact_7d, sub: `${analytics.career_7d} career apps` },
  ] : [];

  const maxDaily = analytics ? Math.max(1, ...analytics.daily.map(d => d.count)) : 1;
  const bySourceEntries = analytics ? Object.entries(analytics.by_source) : [];
  const totalBySource = bySourceEntries.reduce((s, [_, v]) => s + v, 0) || 1;

  return (
    <div data-testid="admin-dashboard" className="p-8 md:p-10">
      <h1 className="font-display font-black text-3xl md:text-4xl">Dashboard</h1>
      <p className="mt-2 text-neutral-600">A snapshot of the site — leads, blog and content.</p>
      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="card-line p-6">
            <div className="flex items-center justify-between">
              <c.icon size={20} weight="bold" className="text-[color:var(--brand)]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Live</span>
            </div>
            <div className="mt-4 font-display font-black text-4xl">{c.val}</div>
            <div className="mt-1 text-sm text-neutral-600">{c.label}</div>
          </Link>
        ))}
      </div>

      {analytics && (
        <>
          <h2 className="mt-12 font-display font-bold text-xl">Funnel · this window</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {analyticsCards.map((c) => (
              <div key={c.label} data-testid={`analytics-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-white border border-[color:var(--line)] p-6">
                <div className="flex items-center justify-between">
                  <c.icon size={18} weight="bold" className="text-[color:var(--brand)]" />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Funnel</span>
                </div>
                <div className="mt-3 font-display font-black text-3xl">{c.val}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">{c.label}</div>
                <div className="mt-2 text-xs text-neutral-500">{c.sub}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-[color:var(--line)] p-6">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-lg">Leads · last 14 days</div>
                <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Daily</span>
              </div>
              <div className="mt-6 flex items-end gap-1 h-40" data-testid="analytics-trend">
                {analytics.daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${d.date}: ${d.count}`}>
                    <div className="w-full bg-gradient-to-t from-[color:var(--brand-dark)] to-[color:var(--brand)] transition-all hover:opacity-80" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count ? "3px" : "1px" }} />
                    <span className="font-mono text-[9px] text-neutral-400">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white border border-[color:var(--line)] p-6">
              <div className="font-display font-bold text-lg">By source</div>
              <div className="mt-4 space-y-3">
                {bySourceEntries.map(([source, count]) => (
                  <div key={source}>
                    <div className="flex justify-between text-xs font-mono uppercase tracking-widest text-neutral-500">
                      <span>{source}</span><span>{count}</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-neutral-100">
                      <div className="h-1.5 bg-[color:var(--brand)]" style={{ width: `${(count / totalBySource) * 100}%` }} />
                    </div>
                  </div>
                ))}
                {bySourceEntries.length === 0 && <div className="text-sm text-neutral-500">No leads yet.</div>}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="mt-10 bg-white border border-[color:var(--line)]">
        <div className="p-5 border-b border-[color:var(--line)] flex items-center justify-between">
          <h2 className="font-display font-bold text-xl">Recent leads</h2>
          <Link to="/admin/leads" className="font-mono text-xs uppercase tracking-widest text-[color:var(--brand)] hover:underline">See all →</Link>
        </div>
        <div className="divide-y divide-[color:var(--line)]">
          {recent.length === 0 && <div className="p-6 text-neutral-500 text-sm">No leads yet — wait for the first submission.</div>}
          {recent.map((l) => (
            <div key={l.id} className="p-5 flex flex-wrap items-center gap-4">
              <span className="font-mono text-[10px] uppercase tracking-widest bg-[color:var(--paper-alt)] px-2 py-1 text-neutral-700">{l.source}</span>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold">{l.name}</div>
                <div className="text-xs text-neutral-500">{l.email} · {String(l.created_at).slice(0, 16).replace("T", " ")}</div>
              </div>
              <div className="text-sm text-neutral-600 truncate max-w-md">{l.message}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
